import { Truck, Building2, Handshake, Trophy } from "lucide-react";
import { fmtMoney } from "@/lib/pricing";

const ICONS = { broker: Handshake, inhouse: Truck, outsourced: Building2 };

function Card({ opt, isBest, numStops, delay }) {
  const Icon = ICONS[opt.key];
  return (
    <div
      className={`kg-rise rounded-lg border p-5 bg-white transition-transform duration-200 hover:-translate-y-1 ${
        isBest ? "border-[#16A34A] ring-2 ring-[#16A34A]/20" : "border-border"
      } ${opt.available ? "" : "opacity-45"}`}
      style={{ animationDelay: `${delay}ms` }}
      data-testid={`comparison-card-${opt.key}`}
    >
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${isBest ? "bg-[#16A34A]" : "bg-[#1C1917]"}`}>
            <Icon className="h-5 w-5 text-[#F97316]" />
          </div>
          <h4 className="font-bold text-[#1C1917]">{opt.label}</h4>
        </div>
        {isBest && (
          <span className="flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[#16A34A] bg-[#DCFCE7] px-2 py-1 rounded">
            <Trophy className="h-3 w-3" /> Best
          </span>
        )}
      </div>

      {opt.available ? (
        <>
          <div className="mt-4">
            <div className="text-3xl font-black text-[#1C1917]" data-testid={`price-${opt.key}`}>
              {fmtMoney(opt.price)}
            </div>
            {numStops > 1 && (
              <div className="text-xs text-[#57534E] mt-0.5">
                {fmtMoney(opt.perStop)} per stop ({numStops} stops)
              </div>
            )}
          </div>
          <div className="mt-4 space-y-1.5 border-t border-border pt-3">
            {opt.breakdown.map((b) => (
              <div key={b.label} className="flex justify-between text-sm">
                <span className={b.muted ? "text-stone-400" : "text-[#57534E]"}>{b.label}</span>
                <span className={`font-medium ${b.amount < 0 ? "text-[#16A34A]" : "text-[#1C1917]"}`}>
                  {b.amount < 0 ? "−" : ""}
                  {fmtMoney(Math.abs(b.amount))}
                </span>
              </div>
            ))}
            {opt.key === "inhouse" && (
              <div className="flex justify-between text-sm pt-1.5 border-t border-border">
                <span className="font-bold text-[#1C1917]">Net margin (after bonus)</span>
                <span className="font-bold text-[#16A34A]">{fmtMoney(opt.netMargin)}</span>
              </div>
            )}
            {opt.otherBids?.length > 0 && (
              <div className="pt-1.5 border-t border-dashed border-border space-y-1" data-testid="other-bids">
                <span className="text-[10px] font-bold uppercase tracking-wide text-stone-400">
                  Other bids
                </span>
                {opt.otherBids.map((b) => (
                  <div key={b.id || `${b.carrierName}-${b.rate}`} className="flex justify-between text-xs text-stone-400">
                    <span>{b.carrierName || "Unnamed carrier"}</span>
                    <span>{fmtMoney(b.rate)}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      ) : (
        <p className="text-sm text-[#57534E] mt-4">
          Fill in the inputs for this option to see a price.
        </p>
      )}
    </div>
  );
}

export default function ComparisonCards({ result }) {
  const { broker, inhouse, outsourced, recommendation, numStops } = result;
  const cards = [broker, inhouse, outsourced];

  return (
    <div className="space-y-4">
      {recommendation && (
        <div
          className="bg-[#DCFCE7] text-[#166534] p-4 border border-[#16A34A]/30 rounded-lg flex items-center gap-3"
          data-testid="recommendation-banner"
        >
          <Trophy className="h-6 w-6 text-[#16A34A] shrink-0" />
          <p className="font-bold text-base md:text-lg">
            Recommendation: <span className="text-[#15803D]">{recommendation.method}</span> at{" "}
            {fmtMoney(recommendation.price)}
            {recommendation.diff != null && (
              <span className="font-medium">
                {" "}
                — saves {fmtMoney(recommendation.diff)} vs {recommendation.nextMethod}
              </span>
            )}
          </p>
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((opt, i) => (
          <Card
            key={opt.key}
            opt={opt}
            numStops={numStops}
            isBest={recommendation?.key === opt.key}
            delay={i * 80}
          />
        ))}
      </div>
    </div>
  );
}
